import React, { useCallback, useEffect, useMemo } from 'react';
import { AppButton, Box, FlexControlledForm, Preloader, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { OrganizationTypes } from '@/common/constants';
import { WarnModal } from '@/components/elements';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerStore } from '@/modules/employer/store';
import { EmailCommunication, EmailCommunicationType } from '@/modules/employer/types/Contact.types';
import useRolesAllowedToCreationQuery from '@/modules/role/queries/userRolesAllowedToCreation.query';
import theme from '@/styles/theme';

import { StyledContent } from '../ContactsSecurity.styles';
import { useVerificationModalStore } from '../stores/useVerificationModal.store';

import { getAccountManagerFubFunction } from './hooks/useContactSecurityForm';
import { useAddContactMutation, useUpdateContactMutation } from './queries/useContacts.queries';
import { ContactResponseModel } from './ContactSecurity.types';
import {
  useContactModalState,
  useContactSecurityForm,
  useEmailCommunicationsFields,
  useHandleErrors,
} from './hooks';

export interface Props {
  onCancel(): void;
  firstContact?: boolean;
  onSubmit?: (data: ContactResponseModel) => void;
  data?: ContactResponseModel;
  openPermissionTable: () => void;
  onDirty: (isDirty: boolean) => void;
  toEdit?: () => void;
  viewMode?: boolean;
  disabledEdit?: boolean;
  adminSsoOnly?: boolean;
}
const ERROR_MODAL_TITLE = 'The server encountered an error processing the request';
const ERROR_MODAL_TEXT = `It doesn't appear to have affected your data, but we cannot save the Employer with the latest records.
                    Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`;
const SERVER_ERROR_CODE = 400;

const AddEditContactsSecurityForm: React.FC<Props> = (props) => {
  const { contactList } = useSetupEmployerStore();
  const {
    values,
    setValues,
    showErrors,
    setShowErrors,
    emailCommunicationsValues,
    setEmailCommunicationsValues,
    handleChangeAllEmailCommunications,
  } = useContactModalState();
  const { employerID } = useSetupEmployerMode();
  const { handleAddSnackbar } = useSnackbar();

  const {
    handleSetErrors,
    errors,
    updateErrorModal,
    setUpdateErrorModal,
    seCreateErrorModal,
    createErrorModal,
  } = useHandleErrors();
  const updateMutation = useUpdateContactMutation(_.toString(props.data?.id));
  const addContactMutation = useAddContactMutation();
  const { options: permissionsList } = useRolesAllowedToCreationQuery({
    isViewMode: false,
    isUserMode: true,
  });

  const onlyOnePrimaryContact = useMemo(
    () => Boolean(contactList.filter((el) => el.primary).length === 1),
    [contactList]
  );

  const hasPrimary = useMemo(() => contactList.some((el) => el.primary), [contactList]);

  const fields = useContactSecurityForm(
    {
      data: props.data || {},
      firstContact: props.firstContact,
      hasPrimary,
      onlyOnePrimaryContact,
      onOpenPermissionTable: props.openPermissionTable,
      viewMode: props.viewMode,
      errors,
      adminSsoOnly: props?.adminSsoOnly,
    },
    permissionsList,
    values,
  );
  const { fields: emailCommunicationsFields } = useEmailCommunicationsFields({
    values: emailCommunicationsValues,
  });
  useEffect(() => {
    const emailCommunicationTypeList = props.data?.email_communication_type_list;

    setEmailCommunicationsValues({
      files: !!emailCommunicationTypeList?.includes(EmailCommunicationType.files),
      finance: !!emailCommunicationTypeList?.includes(EmailCommunicationType.finance),
      planManagement: !!emailCommunicationTypeList?.includes(EmailCommunicationType.planManagement),
      reports: !!emailCommunicationTypeList?.includes(EmailCommunicationType.reports),
    });
  }, [props.data?.email_communication_type_list, setEmailCommunicationsValues]);

  const emailCommunicationsValuesArray = useMemo(
    () => [
      ...(emailCommunicationsValues?.files ? [EmailCommunicationType.files] : []),
      ...(emailCommunicationsValues?.finance ? [EmailCommunicationType.finance] : []),
      ...(emailCommunicationsValues?.planManagement ? [EmailCommunicationType.planManagement] : []),
      ...(emailCommunicationsValues?.reports ? [EmailCommunicationType.reports] : []),
    ],
    [emailCommunicationsValues]
  );

  const isAllEmailCommunicationsSelected = useMemo(
    () => emailCommunicationsValuesArray.length === 3,
    [emailCommunicationsValuesArray]
  );

  const { openVerificationModal } = useVerificationModalStore();

  const handleCreateContact = useCallback(() => {
    if (!values) {
      return;
    }
    setShowErrors(true);
    addContactMutation.mutate(
      {
        organization_id: _.toNumber(employerID),
        ...props?.adminSsoOnly ? {} : { username: values.userName },
        role_id: _.toNumber(values.roleId),
        title: values.title || null,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        // username: contact.userName,
        ...(values.phoneNumber
          ? {
              phone: values.phoneNumber,
            }
          : {}),
        department: values.department || null,
        primary: Boolean(values.primary),
        external_identifier: values.externalIdentifier || null,
        contact_type: OrganizationTypes.employer,
        contact_status_type: 'ACTIVE',
        email_communication_type_list: emailCommunicationsValuesArray,
        job_function_types: getAccountManagerFubFunction(
          values?.jobFunction,
          props?.data?.job_function_types
        ),
      },
      {
        onSuccess: (resp) => {
          if (props.onSubmit) props.onSubmit(resp.data);
        },
        onError: (error) => {
          const code = _.get(error, 'response.status', 0) as number;
          handleSetErrors(error as object, values);
          if (code !== SERVER_ERROR_CODE) {
            seCreateErrorModal(true);
          }
        },
      }
    );
  }, [
    values,
    setShowErrors,
    addContactMutation,
    props,
    handleSetErrors,
    seCreateErrorModal,
    emailCommunicationsValuesArray,
    employerID,
  ]);

  const handleUpdateContact = useCallback(() => {
    if (!props.data?.id || !values) return;
    setShowErrors(true);
    updateMutation.mutate(
      {
        id: props.data?.id,
        organization_id: _.toNumber(employerID),
        title: values.title || null,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        phone: values.phoneNumber || null,
        department: values.department || null,
        primary: Boolean(values.primary),
        external_identifier: values.externalIdentifier || null,
        contact_type: OrganizationTypes.employer,
        contact_status_type: 'ACTIVE',
        ...props?.adminSsoOnly ? {} : { username: values.userName },
        role_id: _.toNumber(values.roleId),
        email_communication_type_list: emailCommunicationsValuesArray,
        job_function_types: getAccountManagerFubFunction(
          values?.jobFunction,
          props?.data?.job_function_types
        ),
      },
      {
        onSuccess: (resp) => {
          if (resp?.data?.email !== props.data?.email || !props.data?.is_email_verified) {
            openVerificationModal({
              email: resp?.data?.email,
              firstName: resp?.data?.first_name,
              lastName: resp?.data?.last_name,
            });
          }
          if (props.onSubmit) props.onSubmit(resp.data);
          handleAddSnackbar({
            text: `${resp?.data?.first_name} ${resp?.data?.last_name} contact details have been updated`,
            closeIcon: true,
            state: SnackbarState.positive,
          });
        },
        onError: (error) => {
          const code = _.get(error, 'response.status', 0) as number;

          handleSetErrors(error as object, values);
          if (code !== SERVER_ERROR_CODE) {
            setUpdateErrorModal(true);
          }
        },
      }
    );
  }, [
    handleSetErrors,
    props,
    setShowErrors,
    setUpdateErrorModal,
    updateMutation,
    values,
    emailCommunicationsValuesArray,
    employerID,
    openVerificationModal,
    handleAddSnackbar,
  ]);
  const handleChangeValues = useCallback(
    (value?: EmailCommunication) => {
      setEmailCommunicationsValues(value);
    },
    [setEmailCommunicationsValues]
  );
  return (
    <>
      <Box
        background="module"
        pad="medium"
        round="moduleRound"
        data-testid="ContactsSecurityModal-form-wrapper"
      >
        <WarnModal
          visible={createErrorModal}
          onSetVisible={seCreateErrorModal}
          header={ERROR_MODAL_TITLE}
          helptext={ERROR_MODAL_TEXT}
          buttonText="Close"
          onSubmit={handleCreateContact}
          submitButtonText="Try again"
        />
        <WarnModal
          visible={updateErrorModal}
          onSetVisible={setUpdateErrorModal}
          header={ERROR_MODAL_TITLE}
          helptext={ERROR_MODAL_TEXT}
          buttonText="Close"
          onSubmit={handleUpdateContact}
          submitButtonText="Try again"
        />
        <StyledContent
          background="canvas"
          round="container1Round"
        >
          <FlexControlledForm
            fields={fields}
            showError={showErrors}
            editMode={!props.viewMode}
            onDirty={props.onDirty}
            onChangeValues={setValues}
            isThinMode
            isModalType
          />
          {/* <FlexForm
            fields={fields}
            editMode
            onSubmit={props.data ? handleUpdateContact : handleCreateContact}
          /> */}
        </StyledContent>
        <StyledContent
          background="canvas"
          key={`${JSON.stringify(emailCommunicationsValues)}`}
          margin={{ top: 'spacing16' }}
          round="container1Round"
        >
          <FlexControlledForm
            formTitle="Email Communications"
            fields={emailCommunicationsFields}
            showError={showErrors}
            editMode={!props.viewMode}
            // shouldControlValues
            onDirty={props.onDirty}
            onChangeValues={handleChangeValues}
            isModalType
            subTitle={
              !props.viewMode && (
                <Box margin={{ top: 'spacing24', bottom: 'spacing16' }}>
                  <Box onClick={() => handleChangeAllEmailCommunications(!isAllEmailCommunicationsSelected)}>
                    <Text color="textAccent">
                      {isAllEmailCommunicationsSelected ? 'Opt-out all' : 'Opt-in all'}
                    </Text>
                  </Box>
                </Box>
              )
            }
            isThinMode
          />
        </StyledContent>
      </Box>
      <div>
        <Box direction="row" justify="end" align="center" pad={{ top: 'medium' }}>
          {props.data && !props.viewMode ? (
            <>
              <AppButton
                testId="ContactsSecurityModal-cancel-edit-mode"
                buttonType="secondary"
                onClick={props.onCancel}
                containerStyle={{ marginRight: theme.spacings.spacing12 }}
              >
                Cancel
              </AppButton>

              <AppButton
                testId="ContactsSecurityModal-save-edit-mode"
                onClick={handleUpdateContact}
                disabled={updateMutation.isLoading}
                type="submit"
              >
                {addContactMutation.isLoading ? <Preloader color="white" /> : 'Save Edits'}
              </AppButton>
            </>
          ) : (
            props.data &&
            !props.disabledEdit && (
              <AppButton testId="ContactsSecurityModal-to-edit" onClick={props.toEdit} type="submit">
                Edit
              </AppButton>
            )
          )}

          {!props.data && (
            <>
              <AppButton
                testId="ContactsSecurityModal-cancel-create-mode"
                buttonType="secondary"
                onClick={props.onCancel}
                containerStyle={{ marginRight: theme.spacings.spacing12 }}
              >
                Cancel
              </AppButton>

              <AppButton
                testId="ContactsSecurityModal-save-create-mode"
                onClick={handleCreateContact}
                disabled={addContactMutation.isLoading}
                type="submit"
              >
                {addContactMutation.isLoading ? <Preloader color="white" /> : 'Save Contact'}
              </AppButton>
            </>
          )}
        </Box>
      </div>
    </>
  );
};

export default AddEditContactsSecurityForm;
